import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, Building2 } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DashboardHeader from './DashboardHeader';
import DateFilter, { formatDisplayDate, isTodayFilter, matchesDateFilter, todayDateFilter } from './DateFilter';
import ExportButtons from './ExportButtons';
import GlobalFilters from './GlobalFilters';
import { defaultGlobalFilters, matchesGlobalFilters, hasActiveGlobalFilters, sameText, uniqueNormalized, spocsFromCompanies } from '../utils/filterUtils';

const STATUS_COLORS = {
    'Pending': '#94a3b8',
    'Work In Progress': '#f59e0b',
    'ToSubmit': '#fbbf24',
    'Submitted': '#6366f1',
    'Completed': '#16a34a'
};

const normalizeStatus = (status) => {
    const s = (status || '').trim().toLowerCase();
    if (s === 'pending' || s === 'assigned to' || s === 'assigned' || s === 'unassigned') return 'Pending';
    if (s.includes('progress') || s === 'wip') return 'Work In Progress';
    if (s === 'tosubmit' || s === 'to submit' || s === 'ready to submit') return 'ToSubmit';
    if (s === 'submitted') return 'Submitted';
    if (s.includes('complete')) return 'Completed';
    return status;
};

const createDateFilterCondition = (filterState, dateField) => (item) => matchesDateFilter(item, filterState, dateField);

const STAGE_REMARK_OPTIONS = [
    'Screen Select - Interview',
    'Screen Reject by Client',
    'Screen Reject - Interview',
    'Glider Assessment - Interview',
    'Glider Reject - Interview',
    'Tech Interview L1 - Interview',
    'Tech Interview L2 - Interview',
    'Tech Interview L3 - Interview',
    'Tech Reject - Interview',
    'Client Round - Interview',
    'Client Reject - Interview',
    'Selected for Role - Client Onboarded',
    'Onboarded Joined Company - Onboarded',
    'Internal Reject'
];

const INTERVIEW_STAGE_VALUES = [
    'Screen Select - Interview', 'Glider Assessment - Interview',
    'Tech Interview L1 - Interview', 'Tech Interview L2 - Interview', 'Tech Interview L3 - Interview',
    'Client Round - Interview'
];
const REJECTED_STAGE_VALUES = [
    'Screen Reject by Client', 'Screen Reject - Interview', 'Glider Reject - Interview',
    'Tech Reject - Interview', 'Client Reject - Interview', 'Internal Reject'
];
const ONBOARDED_STAGE_VALUES = ['Selected for Role - Client Onboarded', 'Onboarded Joined Company - Onboarded'];

const getCandidateBucket = (stageRemark) => {
    if (ONBOARDED_STAGE_VALUES.includes(stageRemark)) return 'onboarded';
    if (INTERVIEW_STAGE_VALUES.includes(stageRemark)) return 'interviews';
    return 'candidates';
};

const selectStyle = { padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', background: '#fff', minWidth: '150px' };
const smallSelectStyle = { ...selectStyle, minWidth: '140px', padding: '5px 7px', fontSize: '11px' };
const inputStyle = { padding: '9px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };

const statusBadgeColors = {
    'Pending': { background: '#e2e8f0', color: '#334155' },
    'Work In Progress': { background: '#fef3c7', color: '#92400e' },
    'ToSubmit': { background: '#fde68a', color: '#92400e' },
    'Submitted': { background: '#c7d2fe', color: '#3730a3' },
    'Completed': { background: '#dcfce7', color: '#166534' }
};
const API = process.env.REACT_APP_BACKEND_API_URL;

const AdminAnalytics = ({ user, onLogout }) => {
    const navigate = useNavigate();
    const { isMobile } = useResponsive();
    const [activeTab, setActiveTab] = useState('analytics');

    const [tasks, setTasks] = useState([]);
    const [kpiDrafts, setKpiDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingStageId, setSavingStageId] = useState(null);
    const [deleteConfirmCandidate, setDeleteConfirmCandidate] = useState(null);
    const [deletingCandidate, setDeletingCandidate] = useState(false);

    const [filterCompany, setFilterCompany] = useState('');
    const [filterClientSpoc, setFilterClientSpoc] = useState('');
    const [filterEvantiSpoc, setFilterEvantiSpoc] = useState('');
    const [filterJobRole, setFilterJobRole] = useState('');
    const [filterTeamLead, setFilterTeamLead] = useState('');
    const [filterRecruiter, setFilterRecruiter] = useState('');
    const [statusTab, setStatusTab] = useState('All');
    const [taskDateFilter, setTaskDateFilter] = useState(todayDateFilter);
    // Received Date / Action Date (the linked job's dates) — shared global filters.
    const [globalFilters, setGlobalFilters] = useState(defaultGlobalFilters);
    const [resetSignal, setResetSignal] = useState(0);

    const [candFilterRemark, setCandFilterRemark] = useState('');
    const [candFilterJobRole, setCandFilterJobRole] = useState('');
    const [candFilterTeamLead, setCandFilterTeamLead] = useState('');
    const [candFilterRecruiter, setCandFilterRecruiter] = useState('');
    const [candFilterCompany, setCandFilterCompany] = useState('');
    const [candFilterVendorName, setCandFilterVendorName] = useState('');
    const [candFilterClientSpoc, setCandFilterClientSpoc] = useState('');
    const [candFilterEvantiSpoc, setCandFilterEvantiSpoc] = useState('');
    const [candidateSubTab, setCandidateSubTab] = useState('candidates'); // 'candidates' | 'interviews' | 'onboarded'

    // Read-only now — there's no master companies table anymore. This is
    // derived from whatever company/vendor SPOC names have actually been
    // used in job_requirements/tasks so far.
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(true);

    const fetchAnalytics = useCallback(() => {
        setLoading(true);
        fetch(`${API}/api/analytics/admin`)
            .then(res => res.json())
            .then(data => {
                setTasks(data.tasks || []);
                setKpiDrafts(data.kpiDrafts || []);
            })
            .catch(err => console.error('Failed to fetch analytics:', err))
            .finally(() => setLoading(false));
    }, []);

    const fetchCompanies = useCallback(() => {
        setLoadingCompanies(true);
        fetch(`${API}/api/companies-summary`)
            .then(res => res.json())
            .then(data => setCompanies(data))
            .catch(err => console.error('Failed to fetch companies summary:', err))
            .finally(() => setLoadingCompanies(false));
    }, []);

    useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);
    useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

    const companyOptions = useMemo(() => Array.from(new Set(tasks.map(t => t.company).filter(Boolean))).sort(), [tasks]);
    // /api/companies returns each company with its SPOCs — the source for the Client SPOC list.
    const [companyMaster, setCompanyMaster] = useState([]);
    useEffect(() => {
        fetch(`${API}/api/companies`)
            .then(res => res.json())
            .then(data => setCompanyMaster(Array.isArray(data) ? data : []))
            .catch(err => console.error('Failed to fetch companies:', err));
    }, []);
    // Client SPOC options: the selected company's SPOCs from the Company Master, unioned with
    // the tasks in view, de-duplicated case/whitespace-insensitively (master spelling first).
    const vendorSpocOptions = useMemo(() => uniqueNormalized(
        spocsFromCompanies(companyMaster, filterCompany),
        tasks.filter(t => !filterCompany || sameText(t.company, filterCompany)).map(t => t.client_spoc)
    ), [companyMaster, tasks, filterCompany]);
    const etSpocOptions = useMemo(() => Array.from(new Set(tasks.map(t => t.evanti_spoc).filter(Boolean))).sort(), [tasks]);
    const jobRoleOptions = useMemo(() => Array.from(new Set(tasks.map(t => t.job_role).filter(Boolean))).sort(), [tasks]);
    const teamLeadOptions = useMemo(() => Array.from(new Set(tasks.map(t => t.assigned_by).filter(Boolean))).sort(), [tasks]);
    const recruiterOptions = useMemo(() => Array.from(new Set(tasks.map(t => t.assigned_to).filter(Boolean))).sort(), [tasks]);
    const candJobRoleOptions = useMemo(() => Array.from(new Set(kpiDrafts.map(k => k.job_role).filter(Boolean))).sort(), [kpiDrafts]);
    const candTeamLeadOptions = useMemo(() => Array.from(new Set(kpiDrafts.map(k => k.assigned_by).filter(Boolean))).sort(), [kpiDrafts]);
    const candRecruiterOptions = useMemo(() => Array.from(new Set(kpiDrafts.map(k => k.assigned_to).filter(Boolean))).sort(), [kpiDrafts]);
    const candCompanyOptions = useMemo(() => Array.from(new Set(kpiDrafts.map(k => k.company).filter(Boolean))).sort(), [kpiDrafts]);
    const candVendorNameOptions = useMemo(() => Array.from(new Set(kpiDrafts.map(k => k.vendor_name).filter(Boolean))).sort(), [kpiDrafts]);
    const candClientSpocOptions = useMemo(() => Array.from(new Set(
        kpiDrafts.filter(k => !candFilterVendorName || k.vendor_name === candFilterVendorName).map(k => k.client_spoc).filter(Boolean)
    )).sort(), [kpiDrafts, candFilterVendorName]);
    const candEvantiSpocOptions = useMemo(() => Array.from(new Set(kpiDrafts.map(k => k.evanti_spoc).filter(Boolean))).sort(), [kpiDrafts]);

    const clearTaskFilters = () => {
        setFilterCompany(''); setFilterClientSpoc(''); setFilterEvantiSpoc(''); setFilterJobRole('');
        setFilterTeamLead(''); setFilterRecruiter(''); setTaskDateFilter(todayDateFilter());
        setGlobalFilters(defaultGlobalFilters());
        setResetSignal(x => x + 1);
    };
    const clearCandidateFilters = () => {
        setCandFilterRemark(''); setCandFilterJobRole('');
        setCandFilterTeamLead(''); setCandFilterRecruiter('');
        setCandFilterCompany(''); setCandFilterVendorName(''); setCandFilterClientSpoc(''); setCandFilterEvantiSpoc('');
    };

    const taskDateCondition = useMemo(() => createDateFilterCondition(taskDateFilter, 'assigned_date'), [taskDateFilter]);

    const filteredTasks = useMemo(() => {
        return tasks.filter(t => {
            if (filterCompany && t.company !== filterCompany) return false;
            if (filterClientSpoc && !sameText(t.client_spoc, filterClientSpoc)) return false;
            if (filterEvantiSpoc && t.evanti_spoc !== filterEvantiSpoc) return false;
            if (filterJobRole && t.job_role !== filterJobRole) return false;
            if (filterTeamLead && t.assigned_by !== filterTeamLead) return false;
            if (filterRecruiter && t.assigned_to !== filterRecruiter) return false;
            if (statusTab !== 'All' && normalizeStatus(t.status) !== statusTab) return false;
            if (!matchesGlobalFilters(t, globalFilters)) return false;
            if (!taskDateCondition(t)) return false;
            return true;
        });
    }, [tasks, filterCompany, filterClientSpoc, filterEvantiSpoc, filterJobRole, filterTeamLead, filterRecruiter, statusTab, taskDateCondition, globalFilters]);

    // Split into the 3 buckets first, then apply team-lead/recruiter/remark
    // filters within whichever bucket is currently active.
    const candidatesByBucket = useMemo(() => {
        const buckets = { candidates: [], interviews: [], onboarded: [] };
        kpiDrafts.forEach(k => {
            buckets[getCandidateBucket(k.stage_remark)].push(k);
        });
        return buckets;
    }, [kpiDrafts]);

    const rejectedCount = useMemo(
        () => kpiDrafts.filter(k => REJECTED_STAGE_VALUES.includes(k.stage_remark)).length,
        [kpiDrafts]
    );

    const filteredCandidates = useMemo(() => {
        return (candidatesByBucket[candidateSubTab] || []).filter(k => {
            if (candFilterRemark && k.stage_remark !== candFilterRemark) return false;
            if (candFilterJobRole && k.job_role !== candFilterJobRole) return false;
            if (candFilterTeamLead && k.assigned_by !== candFilterTeamLead) return false;
            if (candFilterRecruiter && k.assigned_to !== candFilterRecruiter) return false;
            if (candFilterCompany && k.company !== candFilterCompany) return false;
            if (candFilterVendorName && k.vendor_name !== candFilterVendorName) return false;
            if (candFilterClientSpoc && k.client_spoc !== candFilterClientSpoc) return false;
            if (candFilterEvantiSpoc && k.evanti_spoc !== candFilterEvantiSpoc) return false;
            return true;
        });
    }, [candidatesByBucket, candidateSubTab, candFilterRemark, candFilterJobRole, candFilterTeamLead, candFilterRecruiter,
        candFilterCompany, candFilterVendorName, candFilterClientSpoc, candFilterEvantiSpoc]);

    const pieData = useMemo(() => {
        const counts = {};
        filteredTasks.forEach(t => {
            const bucket = normalizeStatus(t.status);
            counts[bucket] = (counts[bucket] || 0) + 1;
        });
        return Object.entries(counts).map(([name, value]) => ({ name, value }));
    }, [filteredTasks]);

    const statusTabs = ['All', 'Pending', 'Work In Progress', 'ToSubmit', 'Submitted', 'Completed'];

    const candidateExportColumns = [
        { key: 'candidate_code', label: 'Candidate ID' }, { key: 'entry_date', label: 'Date' }, { key: 'assigned_by', label: 'Team Lead' },
        { key: 'assigned_to', label: 'Recruiter' }, { key: 'task_display_id', label: 'Task ID' },
        { key: 'company', label: 'Company' },
        { key: 'job_role', label: 'Job Role' }, { key: 'req_id', label: 'Req ID' },
        { key: 'name', label: 'Name' }, { key: 'number', label: 'Number' },
        { key: 'remark', label: 'Call Remark' }, { key: 'skill', label: 'Skill' }, { key: 'email', label: 'Email' },
        { key: 'location', label: 'Location' }, { key: 'current_employer', label: 'Employer' },
        { key: 'designation', label: 'Designation' }, { key: 'total_experience_years', label: 'Exp (Yrs)' },
        { key: 'notice_period', label: 'Notice Period' }, { key: 'education', label: 'Education' },
        { key: 'vendor_name', label: 'Vendor Name' }, { key: 'client_spoc', label: 'Client SPOC' },
        { key: 'evanti_spoc', label: 'Evanti SPOC' }, { key: 'bill_rate', label: 'Evanti Pay / Bill Rate' },
        { key: 'current_package', label: 'Current Pkg' }, { key: 'expected_package', label: 'Expected Pkg' },
        { key: 'details_remarks', label: 'Remarks' }, { key: 'stage_remark', label: 'Remark' },
        { key: 'interview_date', label: 'Interview Date' }, { key: 'interview_time', label: 'Interview Time' }
    ];

    const handleStageRemarkChange = async (candidateId, value) => {
        setSavingStageId(candidateId);
        const current = kpiDrafts.find(k => k.id === candidateId) || {};
        setKpiDrafts(prev => prev.map(k => k.id === candidateId ? { ...k, stage_remark: value } : k));
        try {
            const response = await fetch(`${API}/api/tasks/${current.task_id}/candidates/${candidateId}/stage-remarks`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ stage_remark: value })
            });
            if (!response.ok) { fetchAnalytics(); }
        } catch (err) {
            console.error('Failed to save stage remark:', err);
            fetchAnalytics();
        } finally {
            setSavingStageId(null);
        }
    };

    const handleInterviewDateTimeChange = async (candidateId, field, value) => {
        const current = kpiDrafts.find(k => k.id === candidateId) || {};
        setKpiDrafts(prev => prev.map(k => k.id === candidateId ? { ...k, [field]: value } : k));
        try {
            const response = await fetch(`${API}/api/tasks/${current.task_id}/candidates/${candidateId}/stage-remarks`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    interview_date: field === 'interview_date' ? value : current.interview_date,
                    interview_time: field === 'interview_time' ? value : current.interview_time
                })
            });
            if (!response.ok) fetchAnalytics();
        } catch (err) {
            console.error('Failed to save interview date/time:', err);
            fetchAnalytics();
        }
    };

    // Item #13: Admin-only candidate deletion, with confirmation.
    const handleDeleteCandidate = async () => {
        if (!deleteConfirmCandidate) return;
        setDeletingCandidate(true);
        try {
            const response = await fetch(
                `${API}/api/tasks/${deleteConfirmCandidate.task_id}/candidates/${deleteConfirmCandidate.id}?requester_role=${encodeURIComponent(user?.role || '')}`,
                { method: 'DELETE' }
            );
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                console.error(data.error || 'Failed to delete candidate.');
            }
            fetchAnalytics();
        } catch (err) {
            console.error('Failed to delete candidate:', err);
        } finally {
            setDeletingCandidate(false);
            setDeleteConfirmCandidate(null);
        }
    };

    return (
        <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
            <DashboardHeader user={user} titleText="Analytics & Reports" onLogout={onLogout} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <button
                    onClick={() => navigate('/admin')}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', border: '1px solid #e2e8f0', padding: '8px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: '#334155', cursor: 'pointer' }}
                >
                    <ArrowLeft size={14} /> Back to Dashboard
                </button>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Organization Analytics & Reports</h2>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                <button
                    onClick={() => setActiveTab('analytics')}
                    style={{
                        padding: '10px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: 'none', cursor: 'pointer',
                        background: activeTab === 'analytics' ? '#2563eb' : '#fff', color: activeTab === 'analytics' ? '#fff' : '#334155',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}
                >
                    Analytics
                </button>
                <button
                    onClick={() => setActiveTab('companies')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '10px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', border: 'none', cursor: 'pointer',
                        background: activeTab === 'companies' ? '#2563eb' : '#fff', color: activeTab === 'companies' ? '#fff' : '#334155',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                    }}
                >
                    <Building2 size={14} /> Manage Companies
                </button>
            </div>

            {activeTab === 'analytics' && (
                loading ? (
                    <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px' }}>Loading...</div>
                ) : (
                    <>
                        <GlobalFilters filters={globalFilters} onChange={setGlobalFilters} rows={tasks}
                            show={{ company: false, spoc: false, dates: true }} resetSignal={resetSignal} />
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ marginBottom: '4px', fontSize: '11px', fontWeight: '700', color: '#64748b' }}>Assigned Date</div>
                            <DateFilter key={`assigned-${resetSignal}`} dateField="assigned_date" initialValue={taskDateFilter} onFilterChange={setTaskDateFilter} />
                        </div>

                        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '16px 20px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
                            <select style={selectStyle} value={filterTeamLead} onChange={(e) => setFilterTeamLead(e.target.value)}>
                                <option value="">All Team Leads</option>
                                {teamLeadOptions.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                            <select style={selectStyle} value={filterRecruiter} onChange={(e) => setFilterRecruiter(e.target.value)}>
                                <option value="">All Recruiters</option>
                                {recruiterOptions.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                            <select style={selectStyle} value={filterCompany} onChange={(e) => { setFilterCompany(e.target.value); setFilterClientSpoc(''); }}>
                                <option value="">All Companies</option>
                                {companyOptions.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <select style={selectStyle} value={filterClientSpoc} onChange={(e) => setFilterClientSpoc(e.target.value)}>
                                <option value="">All Company SPOCs</option>
                                {vendorSpocOptions.map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                            <select style={selectStyle} value={filterEvantiSpoc} onChange={(e) => setFilterEvantiSpoc(e.target.value)}>
                                <option value="">All Evanti SPOCs</option>
                                {etSpocOptions.map(e => <option key={e} value={e}>{e}</option>)}
                            </select>
                            <select style={selectStyle} value={filterJobRole} onChange={(e) => setFilterJobRole(e.target.value)}>
                                <option value="">All Job Roles</option>
                                {jobRoleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                            {(filterCompany || filterClientSpoc || filterEvantiSpoc || filterJobRole || filterTeamLead || filterRecruiter || !isTodayFilter(taskDateFilter) || hasActiveGlobalFilters(globalFilters)) && (
                                <button onClick={clearTaskFilters} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#fee2e2', color: '#b91c1c', border: 'none', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                                    <X size={13} /> Clear Filters
                                </button>
                            )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '320px 1fr', gap: '20px', marginBottom: '24px' }}>
                            <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '16px' }}>
                                <h3 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>Task Status Breakdown</h3>
                                {pieData.length === 0 ? (
                                    <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px', padding: '40px 0' }}>No tasks match these filters.</div>
                                ) : (
                                    <>
                                        <ResponsiveContainer width="100%" height={220}>
                                            <PieChart>
                                                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75}>
                                                    {pieData.map((entry) => <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || '#94a3b8'} />)}
                                                </Pie>
                                                <Tooltip />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#475569', lineHeight: '1.6', textAlign: 'center' }}>
                                            Tasks Given: <strong>{filteredTasks.length}</strong>
                                            {pieData.map(p => <span key={p.name}> · {p.name}: <strong>{p.value}</strong></span>)}
                                        </p>
                                    </>
                                )}
                            </div>

                            <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '16px', overflowX: 'auto' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                                    <ExportButtons
                                        rows={filteredTasks}
                                        columns={[
                                            { key: 'display_id', label: 'Task ID' }, { key: 'title', label: 'Title' },
                                            { key: 'assigned_by', label: 'Team Lead' }, { key: 'assigned_to', label: 'Recruiter' },
                                            { key: 'company', label: 'Company' }, { key: 'job_role', label: 'Job Role' },
                                            { key: 'req_id', label: 'Req ID' },
                                            { key: 'client_spoc', label: 'Client SPOC' }, { key: 'evanti_spoc', label: 'Evanti SPOC' },
                                            { key: 'assigned_date', label: 'Date' }, { key: 'status', label: 'Status' }
                                        ]}
                                        filename="organization-tasks"
                                        title="Organization Task Status Report"
                                    />
                                    <div style={{ display: 'flex', gap: '10px', fontSize: '12px', fontWeight: '700' }}>
                                        <span style={{ color: '#059669' }}>Interviews: {candidatesByBucket.interviews.length}</span>
                                        <span style={{ color: '#dc2626' }}>Rejected: {rejectedCount}</span>
                                        <span style={{ color: '#2563eb' }}>Onboarded: {candidatesByBucket.onboarded.length}</span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                                    {statusTabs.map(tab => (
                                        <button
                                            key={tab}
                                            onClick={() => setStatusTab(tab)}
                                            style={{
                                                padding: '6px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '600', border: 'none', cursor: 'pointer',
                                                background: statusTab === tab ? '#2563eb' : '#f1f5f9', color: statusTab === tab ? '#fff' : '#475569'
                                            }}
                                        >
                                            {tab}
                                        </button>
                                    ))}
                                </div>

                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                    <thead>
                                        <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                                            <th style={{ padding: '8px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap' }}>S.No</th>
                                            {['Task ID', 'Title', 'Team Lead', 'Recruiter', 'Company', 'Job Role', 'Date', 'Status'].map(h => (
                                                <th key={h} style={{ padding: '8px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTasks.map((t, snoIdx) => {
                                            const bucket = normalizeStatus(t.status);
                                            const colors = statusBadgeColors[bucket] || { background: '#e2e8f0', color: '#334155' };
                                            return (
                                                <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '8px', color: '#64748b', fontWeight: '600' }}>{snoIdx + 1}</td>
                                                    <td style={{ padding: '8px', fontWeight: '700', color: '#2563eb' }}>{t.display_id}</td>
                                                    <td style={{ padding: '8px' }}>{t.title}</td>
                                                    <td style={{ padding: '8px' }}>{t.assigned_by || '-'}</td>
                                                    <td style={{ padding: '8px' }}>{t.assigned_to || '-'}</td>
                                                    <td style={{ padding: '8px' }}>{t.company || '-'}</td>
                                                    <td style={{ padding: '8px' }}>{t.job_role || '-'}</td>
                                                    <td style={{ padding: '8px' }}>{formatDisplayDate(t.assigned_date)}</td>
                                                    <td style={{ padding: '8px' }}><span style={{ ...colors, padding: '2px 8px', borderRadius: '10px', fontWeight: '700' }}>{t.status}</span></td>
                                                </tr>
                                            );
                                        })}
                                        {filteredTasks.length === 0 && (
                                            <tr><td colSpan={9} style={{ padding: '16px', textAlign: 'center', color: '#94a3b8' }}>No tasks match these filters.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )
            )}

            {activeTab === 'companies' && (
                <div>
                    <div style={{ background: '#eff6ff', color: '#1e40af', padding: '10px 14px', borderRadius: '6px', fontSize: '12px', marginBottom: '16px' }}>
                        This list is derived from companies/Client SPOCs already used in Job Gathering and Tasks —
                        there's no separate master list to manage directly. A new company or SPOC appears here
                        automatically once an Admin uses it while creating a job or task.
                    </div>

                    {loadingCompanies ? (
                        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px' }}>Loading...</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {companies.map(company => (
                                <div key={company.name} style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '18px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                        <Building2 size={16} color="#2563eb" />
                                        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>{company.name}</h3>
                                    </div>

                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {(company.client_spoc || []).length === 0 ? (
                                            <span style={{ fontSize: '12px', color: '#94a3b8' }}>No Client SPOCs used yet.</span>
                                        ) : (
                                            company.client_spoc.map(spoc => (
                                                <span key={spoc} style={{ background: '#eff6ff', color: '#1e40af', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                                                    {spoc}
                                                </span>
                                            ))
                                        )}
                                    </div>
                                </div>
                            ))}
                            {companies.length === 0 && (
                                <div style={{ background: '#fff', borderRadius: '12px', padding: '30px', textAlign: 'center', color: '#94a3b8', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                    No companies used yet. They'll appear here once created via Job Gathering or Tasks.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {deleteConfirmCandidate && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200, padding: '20px', boxSizing: 'border-box' }}>
                    <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.25)' }}>
                        <h3 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>Delete Candidate?</h3>
                        <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#475569', lineHeight: '1.5' }}>
                            This will permanently delete <strong>{deleteConfirmCandidate.name || deleteConfirmCandidate.candidate_code}</strong> from task <strong>{deleteConfirmCandidate.task_display_id}</strong>. This cannot be undone.
                        </p>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => setDeleteConfirmCandidate(null)}
                                disabled={deletingCandidate}
                                style={{ flex: 1, background: '#f1f5f9', color: '#334155', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteCandidate}
                                disabled={deletingCandidate}
                                style={{ flex: 1, background: '#dc2626', color: '#fff', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '600', cursor: deletingCandidate ? 'not-allowed' : 'pointer', opacity: deletingCandidate ? 0.7 : 1 }}
                            >
                                {deletingCandidate ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAnalytics;